import React from "react";
import * as store from "../../framework/customStore";
import * as storeActions from "../../models/store/storeActions";
import * as baseService from "../../services/baseService";
import {
    Alert,
    AlertTitle,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
} from "@mui/material";
import { Translations } from "../../models/translations";
import * as workHoursService from "../../services/workHoursService";
import { OwnerParameters } from "../../models/owner/ownerParameters";
import {
    ISalaryPeriodItem,
    SalaryPeriodItem,
} from "../../models/salaryPeriod/salaryPeriodItem";
import { WorkShiftDtoBasic } from "../../models/workShift/workShift";
import { IWorkHoursItem } from "../../models/workTime/workHoursItem";
import {
    WorkHourSaveData,
    getWorkHourSaveItem,
} from "../../models/workHour/workHourSaveData";
import { t } from "i18next";
import { customHistory } from "../../framework/customHistory";
import { EmployeeListItemDto } from "../../models/employee/employee";

export type WorkTimeListDialogState = "closed" | "open" | "saved";

export interface WorkTimeDialogProps {
    saveDialogState: WorkTimeListDialogState;
    setSaveDialogState: (state: WorkTimeListDialogState) => void;
    savingSalaries: boolean;
    setSavingSalaries: (state: boolean) => void;
    workShifts: WorkShiftDtoBasic[];
    workHours: IWorkHoursItem[];
    filteredEmployees: EmployeeListItemDto[];
    selectedSalaryPeriod: ISalaryPeriodItem;
    reload: () => void;
}

export function WorkTimeListDialog({
    saveDialogState,
    setSaveDialogState,
    savingSalaries,
    setSavingSalaries,
    workShifts,
    workHours,
    filteredEmployees,
    selectedSalaryPeriod,
    reload,
}: WorkTimeDialogProps) {

    // This also locks work times
    function saveHours(salaryPeriod: SalaryPeriodItem) {
        const ownerParameters = new OwnerParameters();
        const employeeIds = filteredEmployees.map((e) => e.id);

        const data = {
            employeeIds,
            salaryPeriodId: salaryPeriod.id,
            workHours:
                workHours
                    ?.filter((wh) => employeeIds.includes(wh.employeeId))
                    .map((w) => getWorkHourSaveItem(w)) ?? [],
            workShiftTimeSlotIds: ownerParameters.getOptionIsFunctionsAreInUse()
                ? []
                : workShifts
                    ?.filter((ws) => employeeIds.includes(ws.employeeId))
                    .flatMap((ws) =>
                        ws.workShiftTimeSlots.map((s) => s.id)
                    ) ?? [],
        } as WorkHourSaveData;

        setSavingSalaries(true);
        workHoursService
            .saveWorkHours(data)
            .then(() => {
                setSaveDialogState("saved");
            })
            .catch((error) => {
                store.customStore.dispatch(
                    storeActions.showErrorMessage(
                        baseService.getErrorMessageFromError(error)
                    )
                );
                return null;
            })
            .finally(() => {
                setSavingSalaries(false);
            });
    }

    return (
        <Dialog
            open={saveDialogState !== "closed"}
            onClose={() => setSaveDialogState("closed")}
            maxWidth="sm"
            fullWidth
        >
            <DialogTitle>{t("workTime.saveSalariesTitle")}</DialogTitle>
            {saveDialogState === "open" && (
                <>
                    <DialogContent>
                        <p>{t("workTime.workHoursApproveConfirmation")}</p>
                        <Alert severity="info">
                            {t("workTime.workHoursApproveWarning")}
                        </Alert>
                    </DialogContent>
                    <DialogActions>
                        <Button
                            disabled={savingSalaries}
                            variant="primary"
                            onClick={() => setSaveDialogState("closed")}
                        >
                            {Translations.Cancel}
                        </Button>
                        <Button
                            disabled={savingSalaries}
                            variant="primary"
                            onClick={() => saveHours(selectedSalaryPeriod)}
                        >
                            {t("workTime.createSalaries")}
                        </Button>
                    </DialogActions>
                </>
            )}
            {saveDialogState === "saved" && (
                <>
                    <DialogContent>
                        <Alert severity="success">
                            <AlertTitle>
                                {t("workTime.saveSalariesSuccessTitle")}
                            </AlertTitle>
                            {t("workTime.saveSalariesSuccessMessage")}
                        </Alert>
                    </DialogContent>
                    <DialogActions>
                        <Button
                            variant="contained"
                            onClick={() =>
                                customHistory.push("/worktimepage/salaries/")
                            }
                        >
                            {t("workTime.goToSalaries")}
                        </Button>
                        <Button
                            variant="contained"
                            onClick={() => {
                                setSaveDialogState("closed");
                                reload();
                            }}
                        >
                            {Translations.Close}
                        </Button>
                    </DialogActions>
                </>
            )}
        </Dialog>
    );
}
